// v0.70 — Drop claim page: mdrop.it/d/{id}
//
// "Bob sent you $25 · for lucy" → create a wallet (or log in) → claim (binds
// this wallet to the drop; first claim wins) → optional profile name → "Send
// Bob your confirmation" (share mdrop.it/c/{id} back into the same chat).
// The confirmation link arriving from the recipient IS the proof of who
// claimed; Bob releases with one slide on the other end.

import { PREPARED_TTL_MS, sponsorOnTouch, type PreparedOp, type Submitted, type WalletAdapter } from "../adapters/WalletAdapter";
import { readCredential } from "../credentialStore";
import { encodeFunctionData, stringToHex } from "viem";
import {
  claimDrop,
  dropClaimed,
  dropConfirmUrl,
  getDrop,
  getDropConfig,
  getNameOf,
  getProfile,
  setProfile,
  type DropView,
} from "../api";
import { canonicalizeCode } from "../codes";
import { emojiFor } from "../emoji";
import { LOGO_M } from "../logo";
import { playDone } from "../sound";
import { formatUsdShort } from "../money";
import { formatFx } from "../fx";
import { shareOrCopy } from "../share";
import { BEARER_CLAIM_ABI, claimKeyFromFragment, signClaim } from "../bearer";
import { isPasskeyCancel, PASSKEY_CANCEL_LINE, recordError } from "../diag"; // v0.90.22
import {
  askDisplayName,
  cachedAddress,
  connectHere,
  createWalletHere,
  escapeHtml,
  hasWalletHere,
} from "./dropShared";

// v0.80.31 — a Drop's amount in its own currency (USD until the row says otherwise).
const fmtDrop = (d: { amount: string; currency?: string }) =>
  d.currency && d.currency !== "USD" ? formatFx(d.currency, Number(d.amount)) : formatUsdShort(Number(d.amount));

// v0.20.6 — the circled share glyph (same asset as Request's Share).
const IC_SHARE_CIRCLE = `<svg viewBox="0 0 249.02 249.02" fill="currentColor" aria-hidden="true"><path d="M124.51,249.02c-17.17,0-33.29-3.23-48.34-9.71-15.06-6.47-28.28-15.42-39.67-26.85-11.39-11.43-20.32-24.66-26.79-39.67C3.24,157.78,0,141.68,0,124.51s3.24-33.28,9.7-48.34c6.47-15.05,15.4-28.28,26.79-39.67s24.62-20.32,39.67-26.79C91.23,3.23,107.34,0,124.51,0s33.28,3.23,48.34,9.7c15.05,6.47,28.28,15.4,39.67,26.79,11.39,11.39,20.32,24.62,26.79,39.67,6.47,15.06,9.7,31.17,9.7,48.34s-3.24,33.26-9.7,48.28c-6.47,15.01-15.4,28.24-26.79,39.67-11.39,11.43-24.62,20.39-39.67,26.85-15.06,6.47-31.17,9.71-48.34,9.71ZM140.38,82.76v12.82h10.01c6.1,0,10.58,1.46,13.43,4.39,3.01,2.93,4.52,7.45,4.52,13.55v41.99c0,3.09-.41,5.74-1.22,7.93-.81,2.2-1.91,3.99-3.3,5.37-2.85,2.93-7.32,4.39-13.43,4.39h-52c-6.02,0-10.42-1.46-13.18-4.39-3.01-2.93-4.52-7.36-4.52-13.31v-41.99c0-3.17.39-5.86,1.16-8.06.77-2.2,1.89-3.99,3.36-5.37,2.77-3.01,7.16-4.52,13.18-4.52h10.25v-12.82h-10.25c-4.8,0-9.12.71-12.94,2.14-3.83,1.42-7,3.36-9.52,5.8-5.29,5.29-7.93,12.9-7.93,22.83v41.99c0,9.69,2.64,17.25,7.93,22.71,5.37,5.21,12.86,7.81,22.46,7.81h52c5.04,0,9.46-.69,13.25-2.08,3.78-1.38,6.9-3.3,9.34-5.74,5.37-5.13,8.06-12.7,8.06-22.71v-41.99c0-10.17-2.69-17.78-8.06-22.83-5.21-5.29-12.74-7.93-22.58-7.93h-10.01ZM124.51,136.6c1.71,0,3.17-.59,4.39-1.77,1.22-1.18,1.83-2.62,1.83-4.33v-63.6l-.24-5.98,5.49,5.98,5.74,5.86c1.06,1.14,2.52,1.71,4.39,1.71,1.63,0,2.99-.51,4.09-1.53,1.1-1.02,1.65-2.34,1.65-3.97,0-.81-.16-1.54-.49-2.2-.33-.65-.81-1.3-1.46-1.95l-21-20.39c-1.38-1.46-2.89-2.2-4.52-2.2s-3.09.73-4.39,2.2l-21,20.26c-1.3,1.3-1.95,2.73-1.95,4.27,0,1.63.55,2.95,1.65,3.97,1.1,1.02,2.46,1.53,4.09,1.53,1.87,0,3.34-.57,4.39-1.71l5.74-5.86,5.49-5.98-.24,5.98v63.6c0,1.71.61,3.15,1.83,4.33,1.22,1.18,2.73,1.77,4.52,1.77Z"/></svg>`;

interface Deps {
  adapter: WalletAdapter;
  dropId: string;
  onGoHome: () => void;
  onEnterAs: (address: string) => void;
}

export function renderDropClaimView(root: HTMLElement, { adapter, dropId, onGoHome, onEnterAs }: Deps) {
  const id = dropId.toUpperCase().replace(/[^0-9A-Z]/g, "");
  // v0.90.21 — a bearer Drop's claim key rides after `#`. Read it once, then
  // take it out of the address bar so it isn't left in history or a screenshot.
  // Kept for this tab only, so a reload after a failed claim still works.
  const CK = `m.ck.${id}`;
  let claimKey = claimKeyFromFragment(location.hash);
  try {
    if (claimKey) sessionStorage.setItem(CK, location.hash.slice(1));
    else claimKey = claimKeyFromFragment(sessionStorage.getItem(CK) ?? "");
  } catch {
    /* storage blocked — the fragment we read is still in hand */
  }
  if (location.hash) history.replaceState(history.state, "", location.pathname + location.search);
  root.innerHTML = `
    <main class="screen pay">
      <div class="brand small">${LOGO_M}</div>
      <p class="loading" id="loading">Opening your Drop…</p>
    </main>`;

  getDrop(id).then(renderByStatus, renderInvalid);

  // "Bob Whitfield 🐙" / "🐙 Someone" — the sender as the recipient sees them.
  const senderLine = (d: DropView) =>
    `<span class="cap">${escapeHtml(d.senderName ?? "Someone")}</span> ${emojiFor(d.senderCode, d.senderEmoji)}`;

  function renderInvalid() {
    root.innerHTML = `
      <main class="screen pay center">
        <div class="brand">${LOGO_M}</div>
        <h1>This link isn't valid</h1>
        <p class="sub">The Drop may have been removed, or the link was cut short. Ask the sender for it again.</p>
        <button id="home" class="btn ghost">Go to M</button>
      </main>`;
    root.querySelector("#home")!.addEventListener("click", onGoHome);
  }

  /** Is the wallet cached on this browser the one this Drop paid / bound? */
  async function cachedIsClaimer(d: DropView): Promise<boolean> {
    const cached = cachedAddress();
    if (!cached || !d.claimerCode) return false;
    try {
      const n = await getNameOf(cached);
      return !!n?.display && canonicalizeCode(n.display) === canonicalizeCode(d.claimerCode);
    } catch {
      return false;
    }
  }

  async function renderByStatus(d: DropView) {
    if (d.bearer) {
      // v0.90.21 — paid on claim: released IS claimed.
      if (d.status === "released") return (await cachedIsClaimer(d)) ? renderArrived(d) : renderTaken(d);
      if (d.status !== "open") return renderGone(d);
      if (!claimKey) return renderNoKey(d);
      return renderClaim(d);
    }
    if (d.status === "released") return renderDone(d);
    if (d.status !== "open" && d.status !== "claimed") return renderGone(d);
    // Already claimed by the wallet cached here → the only useful page is the
    // confirmation link (to re-send it); never show "Claim it" again (v0.70.6).
    if (d.status === "claimed" && d.claimerCode) {
      const cached = cachedAddress();
      if (cached) {
        try {
          const n = await getNameOf(cached);
          if (n?.display && canonicalizeCode(n.display) === canonicalizeCode(d.claimerCode)) {
            return renderConfirmShare(d);
          }
        } catch {
          /* fall through to the claim page */
        }
      }
    }
    return renderClaim(d);
  }

  function renderGone(d: DropView) {
    const why = d.status === "cancelled" ? "was taken back by the sender" : "expired and went back to the sender";
    root.innerHTML = `
      <main class="screen pay center">
        <div class="brand small">${LOGO_M}</div>
        <h1>This Drop ${why}.</h1>
        <p class="sub">Ask ${senderLine(d)} to send it again.</p>
        <button id="home" class="btn ghost block">Go to M</button>
      </main>`;
    root.querySelector("#home")!.addEventListener("click", onGoHome);
  }

  // v0.90.21 — the link reached us without its last part (the key after `#`).
  function renderNoKey(d: DropView) {
    root.innerHTML = `
      <main class="screen pay center">
        <div class="brand small">${LOGO_M}</div>
        <h1>This link is missing its last part.</h1>
        <p class="sub">Ask ${senderLine(d)} to send it again, and open the whole link — some apps cut off the end.</p>
        <button id="home" class="btn ghost block">Go to M</button>
      </main>`;
    root.querySelector("#home")!.addEventListener("click", onGoHome);
  }

  function renderTaken(d: DropView) {
    root.innerHTML = `
      <main class="screen pay center">
        <div class="brand small">${LOGO_M}</div>
        <h1>This Drop has already been claimed.</h1>
        <p class="sub">If that wasn't you, tell ${senderLine(d)} straight away.</p>
        <button id="home" class="btn ghost block">Go to M</button>
      </main>`;
    root.querySelector("#home")!.addEventListener("click", onGoHome);
  }

  // v0.90.21 — "€1 from Bob has arrived." The rest is Bob's to send when WE
  // tell him; nothing prompts him on the claim. Tell Bob shares a link that
  // opens his rest card, carrying our wallet code so his card can check it.
  function renderArrived(d: DropView) {
    const who = d.senderName ?? "the sender";
    const restHeld = !!d.rest && d.restStatus === "held";
    const restSent = !!d.rest && d.restStatus === "sent";
    const restFig = d.rest ? fmtDrop({ amount: d.rest, currency: d.currency }) : "";
    // v1.0.2 — the success card, as on the pay page and the sender's rest:
    // "You've got $1.00 from / ATG 🦧", the note, and the two ways on.
    root.innerHTML = `
      <main class="screen pay dc-claimed dc-arrived">
        <div class="do-stack">
          <div class="brand small">${LOGO_M}</div>
          <div class="do-card">
            <div class="tickbig">✓</div>
            <div class="do-h succ">You've got <span class="amt">${escapeHtml(fmtDrop(d))}</span> from</div>
            <div class="do-name succ">${senderLine(d)}</div>
            ${
              restHeld
                ? `<div class="do-s"><span class="cap">${escapeHtml(who)}</span> is sending ${escapeHtml(restFig)} more — let them know you've got this one.</div>`
                : restSent
                  ? `<div class="do-s">…and the other ${escapeHtml(restFig)} has been sent too.</div>`
                  : ""
            }
            <div class="dc-arrived-actions">
              <button id="tell" class="btn ${restHeld ? "primary" : "ghost"} block">Tell <span class="cap">${escapeHtml(who)}</span></button>
              <button id="home" class="btn ${restHeld ? "ghost" : "primary"} block">Open my wallet</button>
            </div>
            <span id="toast" class="copystatus" role="status" aria-live="polite"></span>
          </div>
        </div>
      </main>`;
    const toast = (m: string) => {
      const t = root.querySelector<HTMLElement>("#toast")!;
      t.textContent = m;
      setTimeout(() => (t.textContent = ""), 1600);
    };
    root.querySelector("#tell")!.addEventListener("click", async () => {
      const code = d.claimerCode ? canonicalizeCode(d.claimerCode) : "";
      const url = `${dropConfirmUrl(location.origin, d.id)}${code ? `/${code}` : ""}`;
      const r = await shareOrCopy({ url, text: `Got your ${fmtDrop(d)} on M® 👍`, title: "M®" });
      if (r === "copied") toast("Link copied ✓");
      else if (r === "failed") toast("Couldn't share — long-press to copy");
    });
    root.querySelector("#home")!.addEventListener("click", () => void enter());
  }

  /**
   * v0.90.21 — claim a bearer Drop to `address`: the link's key signs
   * Claim(id, address), and OUR wallet submits it (one Face ID; the paymaster
   * covers gas). M relays nothing and holds no key.
   *
   * v1.0.5 — as fast as a Send. While the card is read, the wallet cached on
   * this browser is connected (no Face ID), the own-Drop check and the name
   * lookups run, and the claim's userOp is PREPARED (nonce, gas, sponsorship —
   * and a simulation, so a Drop claimed a moment ago fails here, not after
   * Face ID). The tap only signs. The card shows at ACCEPTANCE; the receipt
   * and the Worker's bookkeeping follow in the background, and a claim that
   * reverts after all (someone else was first) turns the page into "already
   * claimed". Nothing is signed or submitted before the tap.
   */
  type ClaimPrep = { address: string; myCode: string | null; hasName: boolean; own: boolean; prepared: PreparedOp | null };
  let claimPrep: Promise<ClaimPrep | null> | null = null;
  async function claimCall(d: DropView, contract: string, address: string) {
    const idBytes32 = stringToHex(d.id, { size: 32 });
    const sig = await signClaim(claimKey!, contract, idBytes32, address);
    return {
      to: contract as `0x${string}`,
      data: encodeFunctionData({ abi: BEARER_CLAIM_ABI, functionName: "claim", args: [idBytes32, address as `0x${string}`, sig] }),
    };
  }
  const isOwn = (code: string | null | undefined, d: DropView) =>
    !!code && !!d.senderCode && canonicalizeCode(code) === canonicalizeCode(d.senderCode);
  // v1.1.11 — the preparation was redone every 45 s while the card was read
  // (a prepared userOp was trusted 60 s). v2.0 (22 Oct, the Gas Station's
  // cap) — prepared with Circle's stub only, it keeps for minutes
  // (PREPARED_TTL_MS, its nonce checked at the touch) and reserves nothing;
  // the sponsor's signature is asked for when the finger lands on the button.
  function prepareClaim(d: DropView) {
    const cred = readCredential();
    if (claimPrep || !cred || !claimKey || !d.bearer || d.status !== "open") return;
    claimPrep = (async (): Promise<ClaimPrep> => {
      const { address } = await adapter.connectWithCredential(cred);
      const [me, prof, cfg] = await Promise.all([
        getNameOf(address).catch(() => null),
        getProfile(address).catch(() => null),
        getDropConfig(),
      ]);
      const myCode = me?.display ?? null;
      const base = { address, myCode, hasName: !!prof?.name, own: isOwn(myCode, d), prepared: null };
      const contract = cfg.bearer?.[d.currency ?? "USD"]?.contract;
      if (base.own || !contract) return base;
      const prepared = await adapter.prepare([await claimCall(d, contract, address)]).catch((e) => {
        console.warn("[M] claim prepare failed", e); // claimed already, or the network — the tap takes the plain path
        return null;
      });
      return { ...base, prepared };
    })().catch((e) => {
      console.warn("[M] claim prep failed", e);
      return null;
    });
  }
  async function submitClaim(d: DropView, address: string, prep: ClaimPrep | null): Promise<Submitted> {
    if (prep?.prepared && prep.address.toLowerCase() === address.toLowerCase()) {
      try {
        return await adapter.sendPrepared(prep.prepared);
      } catch (e) {
        if (isPasskeyCancel(e)) throw e;
        if (!(e instanceof Error && e.message === "prepared_stale")) recordError("drop/claim/prepared", e);
      }
    }
    if (!prep && d.senderCode) {
      const mine = await getNameOf(address).catch(() => null);
      if (isOwn(mine?.display, d)) throw new Error("own_drop");
    }
    const cfg = await getDropConfig();
    const contract = cfg.bearer?.[d.currency ?? "USD"]?.contract;
    if (!contract || !claimKey) throw new Error("not_open");
    try {
      return await adapter.submit([await claimCall(d, contract, address)]);
    } catch (e) {
      // Someone may have claimed it first — say so rather than "try again".
      if (isPasskeyCancel(e)) throw e;
      const now = await getDrop(d.id).catch(() => null);
      if (now && now.status !== "open") throw new Error(now.status === "released" ? "already_claimed" : "not_open");
      throw e;
    }
  }

  function renderDone(d: DropView) {
    root.innerHTML = `
      <main class="screen pay center">
        <div class="brand small">${LOGO_M}</div>
        <div class="tickbig">✓</div>
        <h1><span class="amt">${escapeHtml(fmtDrop(d))}</span> is in your wallet.</h1>
        <p class="sub">From ${senderLine(d)}${d.ref ? ` · ${escapeHtml(d.ref)}` : ""}</p>
        <button id="home" class="btn primary block">Open my wallet</button>
      </main>`;
    root.querySelector("#home")!.addEventListener("click", () => void enter());
  }

  async function enter() {
    try {
      const { address } = await connectHere(adapter, false);
      onEnterAs(address);
    } catch {
      onGoHome();
    }
  }

  async function renderClaim(d: DropView) {
    const known = await hasWalletHere();
    root.innerHTML = `
      <main class="screen pay dc">
        <div class="brand small">${LOGO_M}</div>
        <!-- v1.1.3 — one card: the hero, the actions and the reassurance as a
             muted footer (the Sending card's terms-line style). -->
        <section class="balance paycard dc-card">
          <div class="label">${senderLine(d)} sent you</div>
          <div class="pay-amount">${escapeHtml(fmtDrop(d))}</div>
          ${d.ref ? `<div class="do-ref"><span class="pc-k">Ref:</span> ${escapeHtml(d.ref)}</div>` : ""}
          <div class="dc-actions">
            ${
              known
                ? `<button id="claim" class="btn primary block">Put it in your wallet</button>
                   <button id="other" class="linkish status dc-alt" type="button">Use another wallet</button>`
                : `<button id="create" class="btn primary block">Create M® wallet &amp; claim</button>
                   <button id="claim" class="btn ghost block">I already have M® — log in</button>`
            }
          </div>
          <p class="pc-first-note dc-foot"><span class="fn-body"><span class="fn-mark" aria-hidden="true">i</span>No app to install. Your money stays yours — M® never holds it.</span></p>
        </section>
        <p id="status" class="status" role="status" aria-live="polite"></p>
        <div id="dropOverlay" class="drop-overlay" hidden></div>
      </main>`;

    const statusEl = root.querySelector<HTMLElement>("#status")!;
    const busy = (on: boolean) =>
      root.querySelectorAll<HTMLButtonElement>(".dc-actions button").forEach((b) => (b.disabled = on));

    async function claimWith(address: string, prep: ClaimPrep | null = null) {
      statusEl.textContent = d.bearer ? "Claiming — confirm with Face ID…" : "Claiming…";
      let view: DropView | null = null;
      let sub: Submitted | null = null;
      try {
        if (prep?.own) throw new Error("own_drop");
        if (d.bearer) sub = await submitClaim(d, address, prep);
        else view = await claimDrop(id, address);
      } catch (e) {
        console.warn("[M] claim failed", e);
        recordError("drop/claim", e); // v0.90.22 — the claim's cause chain reaches Lab
        const msg = String((e as Error).message ?? "");
        statusEl.textContent = isPasskeyCancel(e)
          ? PASSKEY_CANCEL_LINE.replace("Slide", "Tap")
          : /own_drop/.test(msg)
          ? "That's your own Drop — it can only be claimed by someone else."
          : /already_claimed/.test(msg)
            ? "This Drop has already been claimed with a different wallet."
            : /not_open/.test(msg)
              ? "This Drop is no longer open."
              : "Couldn't claim just now. Please try again.";
        return;
      }
      if (!prep?.hasName) await maybeAskName(address);
      if (sub) {
        // v1.0.5 — "You've got $1.00" at acceptance; the rest in the background.
        const code = prep?.myCode ?? (await getNameOf(address).catch(() => null))?.display ?? null;
        renderArrived({ ...d, status: "released", claimerCode: code });
        playDone(); // 1.5.10 — M's "done": the Drop is in the wallet
        void sub.settled
          .then(async ({ txHash, success }) => {
            if (success) {
              await dropClaimed(d.id, txHash).catch(() => {});
              return;
            }
            recordError("drop/claim/settle", new Error("userop_reverted " + txHash));
            const now = await getDrop(d.id).catch(() => null);
            if (now && now.status === "released") return renderTaken(now);
            claimPrep = null; // spent: the next tap prepares afresh
            await renderClaim(d);
            const st = root.querySelector<HTMLElement>("#status");
            if (st) st.textContent = "That didn't go through — nothing moved. Tap to try again.";
          })
          .catch((e) => recordError("drop/claim/settle", e));
        return;
      }
      renderConfirmShare(view!);
    }

    // v1.0.5 — prepared while this card is read (a wallet known here only).
    if (known) prepareClaim(d);
    // v2.0 — sponsor on touch: the finger on the button asks for the paymaster's signature of what is prepared.
    root.querySelector("#claim")!.addEventListener("pointerdown", () => {
      if (claimPrep) sponsorOnTouch(adapter, claimPrep.then((p) => p?.prepared ?? null));
    });
    root.querySelector("#claim")!.addEventListener("click", async () => {
      busy(true);
      statusEl.textContent = cachedAddress() ? "" : "Log in — choose your passkey…";
      try {
        // v1.1.11 — Lab: how the tap reached Face ID (prepared, waited for the
        // preparation, or the plain path), so a slow one says which.
        const t0 = performance.now();
        const pending = claimPrep;
        const prep = pending ? await pending : null;
        const waited = Math.round(performance.now() - t0);
        const how = !prep ? "plain (no preparation)" : !prep.prepared ? "plain (preparation failed)" : Date.now() - prep.prepared.at > PREPARED_TTL_MS ? "plain (preparation stale)" : "prepared";
        if (how !== "prepared" || waited > 300) recordError("drop/claim/timing", `${how}; waited ${waited} ms for the preparation`);
        const { address } = prep ? { address: prep.address } : await connectHere(adapter, false);
        await claimWith(address, prep);
      } catch (e) {
        console.warn("[M] claim: connect failed", e);
        statusEl.textContent = "Not logged in. Tap again to retry.";
      } finally {
        busy(false);
      }
    });
    root.querySelector("#other")?.addEventListener("click", async () => {
      busy(true);
      statusEl.textContent = "Choose a wallet…";
      try {
        const { address } = await connectHere(adapter, true);
        await claimWith(address);
      } catch {
        statusEl.textContent = "No wallet chosen.";
      } finally {
        busy(false);
      }
    });
    root.querySelector("#create")?.addEventListener("click", async () => {
      // v0.70.12 — name first (it goes onto the passkey), then Face ID.
      const name = await askDisplayName(root);
      askedName = true;
      root.innerHTML = `
        <main class="screen pay center">
          <div class="brand small">${LOGO_M}</div>
          <p class="loading">Creating your wallet — waiting for Face ID…</p>
        </main>`;
      try {
        const { address } = await createWalletHere(adapter, name);
        await renderClaim(d); // back to the claim card, now with a wallet here
        const st = root.querySelector<HTMLElement>("#status");
        if (st) st.textContent = "Claiming…";
        await claimWith(address);
      } catch (e) {
        console.error(e);
        await renderClaim(d);
        const st = root.querySelector<HTMLElement>("#status");
        if (st) st.textContent = "Couldn't create the wallet. Please try again.";
      }
    });
  }

  // First-time profile: a name the sender (and anyone else you pay) will see.
  // Optional; Skip is fine. Never a search key.
  let askedName = false; // v0.70.12 — asked before the ceremony on the create path
  async function maybeAskName(address: string): Promise<void> {
    if (askedName) return;
    let existing: string | null = null;
    try {
      existing = (await getProfile(address)).name;
    } catch {
      /* offline profile read — just ask */
    }
    if (existing) return;
    return new Promise((resolve) => {
      root.innerHTML = `
        <main class="screen pay center">
          <div class="brand small">${LOGO_M}</div>
          <h1>Your name on M®</h1>
          <p class="sub">Shown to people you pay and who pay you.<br/>You can change it any time in Settings.</p>
          <input id="pname" class="nick-input" type="text" placeholder="e.g. Lucy Parker" maxlength="40" autocomplete="name" />
          <div class="actions">
            <button id="save" class="btn primary block">Save</button>
            <button id="skip" class="btn ghost block">Skip for now</button>
          </div>
        </main>`;
      const input = root.querySelector<HTMLInputElement>("#pname")!;
      const done = async (save: boolean) => {
        const v = input.value.trim();
        if (save && v) await setProfile({ address, name: v }).catch(() => {});
        resolve();
      };
      root.querySelector("#save")!.addEventListener("click", () => void done(true));
      root.querySelector("#skip")!.addEventListener("click", () => void done(false));
      input.addEventListener("keydown", (e) => {
        if (e.key === "Enter") void done(true);
      });
      input.focus();
    });
  }

  function renderConfirmShare(d: DropView) {
    const url = dropConfirmUrl(location.origin, d.id);
    const who = d.senderName ?? "the sender";
    root.innerHTML = `
      <main class="screen pay dc-claimed">
        <div class="brand small">${LOGO_M}</div>
        <div class="do-card">
          <div class="do-h">Claimed.</div>
          <div class="do-name small">Now send ${senderLine(d)}<br/>your confirmation</div>
          <div class="do-note">That protects new payees. When ${escapeHtml(who)} releases the money, it instantly lands in your wallet.<br/>Future transfers won't need this step.</div>
          <div class="req-link">${escapeHtml(url.replace(/^https?:\/\//, ""))}</div>
          <div class="req-actions single">
            <button id="share" class="sharebtn" type="button" aria-label="Share confirmation">${IC_SHARE_CIRCLE}</button>
          </div>
          <span id="toast" class="copystatus" role="status" aria-live="polite"></span>
        </div>
      </main>`;
    const toast = (m: string) => {
      const t = root.querySelector<HTMLElement>("#toast")!;
      t.textContent = m;
      setTimeout(() => (t.textContent = ""), 1600);
    };
    root.querySelector("#share")!.addEventListener("click", async () => {
      const r = await shareOrCopy({
        url,
        text: `I've claimed your ${fmtDrop(d)} on M® — tap to release it`,
        title: "M® confirmation",
      });
      if (r === "copied") toast("Link copied ✓");
      else if (r === "failed") toast("Couldn't share — long-press the link");
    });
  }
}
